import './index.css'

import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { useDispatch, useSelector } from 'hooks/use-redux'
import { useDebouncedCallback } from 'use-debounce'
import type { Track } from 'types/track'
import { seekTrackPosition } from '@disco/state-management/actions/MediaActions'
import { PLAYING, SEEKING } from '@disco/constants/MediaStatus'
import { getTrackPlaybackStatus } from '@disco/state-management/selectors/media'
import { useInterval } from 'hooks/use-interval'
import Waveform from '../waveform'
import ScrollableWaveformProgress from './scrollable-waveform-progress-indicator'
type Props = {
  track: Track
}

const ScrollableWaveform = (props: Props): React.ReactElement<'div'> => {
  const { track } = props
  const r = useRef(null)
  const container = useRef(null)
  // const waveformRef = useRef(null)
  const [waveformLength, setWayformLength] = useState(0)
  const [scrollPosition, setScrollPosition] = useState(0)
  const [seekScheduled, setSeekScheduled] = useState(false)
  const [scrollStart, setScrollStart] = useState(0)
  const dispatch = useDispatch()
  const dragging = useRef(false)
  const [ignoreScrollEvent, setIgnoreScrollEvent] = useState(false)
  useEffect(() => {
    // reset scroll position on track change without waiting update tick
    setScrollPosition(0)
  }, [track.id])
  const seekDebounced = useDebouncedCallback((sec: number) => {
    dispatch(seekTrackPosition(track.id, sec))
    setSeekScheduled(false)
  }, 300)

  const seek = (sec: number) => {
    setSeekScheduled(true)
    seekDebounced(sec)
  }

  const getMillis = (position: number) => {
    const x = position
    let adjustedX = x
    if (x < 0) adjustedX = 0
    if (x > waveformLength) adjustedX = waveformLength
    const pos = (adjustedX / waveformLength) * track.duration * 1000
    return Math.round(pos)
  }

  const onScroll = (event: React.SyntheticEvent<HTMLDivElement>) => {
    const target = event.nativeEvent.target as HTMLDivElement
    const millis = getMillis(target.scrollLeft)
    setScrollPosition(millis)

    if (ignoreScrollEvent) {
      return setIgnoreScrollEvent(false)
    }
  }

  useLayoutEffect(() => {
    if (!r.current) return
    const { width } = r.current.getBoundingClientRect()
    setWayformLength(width)
  }, [r])
  const status = useSelector(state =>
    getTrackPlaybackStatus(state, {
      trackId: track.id,
    }),
  )
  const updateProgress = useCallback(() => {
    if (status?.playbackStatus !== PLAYING) return

    if (
      container.current &&
      !dragging.current &&
      (status?.playbackStatus as string) !== SEEKING &&
      !seekScheduled
    ) {
      setIgnoreScrollEvent(true)

      if (container.current) {
        container.current.scrollLeft =
          (status?.playbackPosition / track.duration) * waveformLength
      }
    }
  }, [
    status,
    seekScheduled,
    dragging,
    container,
    track.duration,
    waveformLength,
  ])
  useInterval(updateProgress, 1000)
  return (
    <div className="ScrollableWaveform" ref={r}>
      <ScrollableWaveformProgress
        progress={status?.playbackPosition || 0}
        duration={track.duration}
      />
      <div
        className="ScrollableWaveform_Container"
        onScroll={onScroll}
        onTouchMove={e => {
          dragging.current = true
          const x = e.changedTouches[0].clientX
          if (container.current) container.current.scrollBy(scrollStart - x, 0)
          setScrollStart(e.changedTouches[0].clientX)
        }}
        onTouchEnd={() => {
          dragging.current = false
          const millis = getMillis(container.current?.scrollLeft ?? 0)
          setScrollPosition(millis)
          seek(millis / 1000)
        }}
        onTouchStart={e => {
          dragging.current = true
          setScrollStart(e.changedTouches[0].clientX)
        }}
        onTouchCancel={() => {
          dragging.current = false
        }}
        ref={container}
      >
        <div className="ScrollableWaveform_Wrapper">
          <div className="ScrollableWaveform_Spacer" />
          <Waveform
            hover={scrollPosition / 1000}
            scrollable
            className="Player-waveform Player-waveform--scrollable"
            track={track}
            barWidth={1}
            barGap={2}
            noAnimation
          />
          <div className="ScrollableWaveform_Spacer" />
        </div>
      </div>
    </div>
  )
}

export default ScrollableWaveform
